import React from 'react';
import { Container, Typography, Paper, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export default function MuiRhfAdvanced2Docs() {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          MUI + RHF Advanced - Part 2
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>
          🏠 HOME
        </Button>
      </Box>

      <Typography variant="body1" sx={{ mb: 3 }}>
        고급 MUI 컴포넌트들과 React Hook Form을 연동하는 방법을 학습합니다. 
        Autocomplete, DatePicker, 파일 업로드 등 복잡한 컴포넌트들을 완벽하게 제어해보겠습니다.
      </Typography>

      <Typography variant="h4" gutterBottom sx={{ mt: 4, mb: 2 }}>
        고급 컴포넌트 연동
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Autocomplete 연동
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Autocomplete는 복잡한 상태 관리가 필요한 컴포넌트입니다. 
        검색, 다중 선택, 커스텀 옵션 등의 기능을 React Hook Form과 연결해봅시다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { Autocomplete, TextField, Chip } from '@mui/material';
import { Controller } from 'react-hook-form';

function CategoryAutocomplete() {
  const { control } = useForm();
  const [categories, setCategories] = useState([]);

  const searchCategories = async (query) => {
    const response = await fetch(\`/api/categories/search?q=\${query}\`);
    const data = await response.json();
    setCategories(data);
  };

  return (
    <Controller
      name="categories"
      control={control}
      defaultValue={[]}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <Autocomplete
          multiple
          options={categories}
          getOptionLabel={(option) => option.name}
          value={value}
          onChange={(event, newValue) => onChange(newValue)}
          onInputChange={(event, newInputValue) => {
            if (newInputValue) {
              searchCategories(newInputValue);
            }
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option.name}
                {...getTagProps({ index })}
                key={option.id}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="카테고리"
              placeholder="카테고리를 검색하세요"
              error={!!error}
              helperText={error?.message}
            />
          )}
          freeSolo
          selectOnFocus
        />
      )}
    />
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        DatePicker 연동
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        날짜 선택기는 외부 라이브러리를 사용하므로 특별한 주의가 필요합니다. 
        유효성 검사와 포맷팅도 함께 처리해봅시다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

function ProductDatesForm() {
  const { control, watch } = useForm();
  const startDate = watch('startDate');

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Controller
            name="startDate"
            control={control}
            rules={{
              required: '시작 날짜는 필수입니다',
              validate: (value) => {
                const today = new Date();
                if (value < today) {
                  return '시작 날짜는 오늘 이후여야 합니다';
                }
                return true;
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                {...field}
                label="판매 시작일"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
                minDate={new Date()}
              />
            )}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        파일 업로드 연동
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        파일 업로드는 특별한 처리가 필요합니다. 
        드래그 앤 드롭, 미리보기, 진행률 표시 등의 기능을 포함해봅시다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { useState } from 'react';
import { Box, Button, LinearProgress } from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';

function FileUploadField() {
  const { control } = useForm();
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = (e.loaded / e.total) * 100;
          setUploadProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          resolve(response.url);
        } else {
          reject(new Error('업로드 실패'));
        }
      });

      xhr.open('POST', '/api/upload');
      xhr.send(formData);
    });
  };

  return (
    <Controller
      name="images"
      control={control}
      defaultValue={[]}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <Box>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUpload />}
            sx={{ width: '100%', height: 120 }}
          >
            이미지 업로드
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={async (e) => {
                const files = Array.from(e.target.files);
                const uploadedUrls = [];
                
                for (const file of files) {
                  try {
                    const url = await uploadFile(file);
                    uploadedUrls.push(url);
                  } catch (error) {
                    console.error('업로드 실패:', error);
                  }
                }
                
                onChange([...value, ...uploadedUrls]);
              }}
            />
          </Button>
          
          {uploadProgress > 0 && uploadProgress < 100 && (
            <LinearProgress variant="determinate" value={uploadProgress} />
          )}
        </Box>
      )}
    />
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        실시간 계산 폼
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        watch를 사용하여 폼 값의 변화를 감지하고 실시간으로 계산 결과를 보여주는 방법입니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`function PriceCalculatorForm() {
  const { control, watch, setValue } = useForm({
    defaultValues: {
      quantity: 1,
      unitPrice: 0,
      discountRate: 0,
      taxRate: 10
    }
  });

  const watchedValues = watch(['quantity', 'unitPrice', 'discountRate', 'taxRate']);
  const [quantity, unitPrice, discountRate, taxRate] = watchedValues;

  useEffect(() => {
    // 소계 계산
    const subtotal = (quantity || 0) * (unitPrice || 0);
    
    // 할인 금액 계산
    const discountAmount = subtotal * ((discountRate || 0) / 100);
    
    // 세전 금액
    const afterDiscount = subtotal - discountAmount;
    
    // 세금 계산
    const taxAmount = afterDiscount * ((taxRate || 0) / 100);
    
    // 최종 금액
    const total = afterDiscount + taxAmount;
    
    // 계산된 값들 자동 업데이트
    setValue('subtotal', Math.round(subtotal));
    setValue('discountAmount', Math.round(discountAmount));
    setValue('taxAmount', Math.round(taxAmount));
    setValue('total', Math.round(total));
  }, watchedValues);

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Controller
          name="quantity"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="수량"
              type="number"
              fullWidth
            />
          )}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="h6">계산 결과</Typography>
          <Typography>총액: ₩{Math.round((quantity * unitPrice) * (1 - discountRate / 100) * (1 + taxRate / 100)).toLocaleString()}</Typography>
        </Paper>
      </Grid>
    </Grid>
  );
}`}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/docs/mui-rhf-advanced-1')}
        >
          ← PART 1: 복잡한 폼 구조
        </Button>
        <Button 
          variant="contained" 
          onClick={() => navigate('/docs/mui-rhf-advanced-3')}
        >
          PART 3: 에러 처리 및 최적화 →
        </Button>
      </Box>
    </Container>
  );
}
